import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import { deleteActivation } from "@/api";
import { DialogComponentProps, useSnackbar } from "@/context";
import { useActivationCache } from "@/hooks";

type DeleteActivationDialogProps = DialogComponentProps<"deleteActivation">;

const DeleteActivationDialog: React.FC<DeleteActivationDialogProps> = ({
  activation,
  open,
  onClose,
  redirect,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const cache = useActivationCache();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleDelete = async () => {
    if (!activation) return;

    setIsDeleting(true);
    const client = process.env.REACT_APP_CLIENT || 'core';

    const token = await getAccessTokenSilently();
    const { data, error } = await deleteActivation(
      token,
      activation.campaign_id || "",
      activation.campaign_name || "",
      activation.campaign_type || "",
      client,
    );

    if (data) {
      cache.remove(activation.campaign_id, data);
      setIsDeleting(false);
      if (redirect) {
        onClose();
        navigate(redirect);
      } else {
        onClose();
      }
    }

    if (error) {
      setIsDeleting(false);
      openSnackbar(error.message, "error");
    }
  };

  return (
    <Dialog open={open} disableEnforceFocus aria-labelledby="delete-dialog">
      <DialogTitle sx={{ background: theme.palette.error.dark }}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <ErrorOutline sx={{ mr: 1 }} />
          <Typography variant="body1">
            Are you sure you want to delete this activation?
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Typography>
          Please confirm you wish to delete <b>{activation.campaign_name}</b>{" "}
          from your saved activations. If the activation is still processing or
          live, it will remain in the platform until the expiration date.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          loading={isDeleting}
        >
          Delete Activation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteActivationDialog;
